
/**
 * 터미널 에뮬레이터 배선 — ADR 0032
 * --------------------------------------------------------------------------
 * React 바깥의 명령형 객체를 한 곳에 가둡니다. 컴포넌트는 만들기·쓰기·크기 보고만 알고,
 * xterm API는 이 파일 밖으로 새지 않습니다.
 *
 * **키 입력을 해석하지 않습니다.** `onData`가 주는 바이트를 그대로 stdin 채널로 보냅니다 —
 * Tab 자동완성·`clear`·`↑` 히스토리·`Ctrl-C`는 전부 셸이 하는 일이고, 브라우저가 줄을
 * 모아 보내는 동안에는 그 바이트가 셸에 닿은 적이 없었습니다.
 *
 * **에뮬레이터는 늦게 싣습니다.** Debug Terminal은 opt-in 패널이고 대부분의 방문에서 열리지
 * 않으므로, 첫 화면이 280KB를 함께 내려받을 이유가 없습니다. 패널을 여는 순간 한 번만
 * 가져옵니다.
 *
 * 색은 역할 토큰에서 읽습니다. 에뮬레이터는 CSS 변수를 모르므로 계산된 값을 넘기고,
 * Light/Dark 전환에 맞춰 다시 읽습니다 — 두 모드에 한 벌의 색을 박으면 한쪽이 안 보입니다.
 */

/** 화면에 남기는 스크롤백. 넘긴 줄은 에뮬레이터가 버립니다. */
const SCROLLBACK = 2000;

export interface TerminalHandle {
  write(chunk: string): void;
  focus(): void;
  reset(): void;
  /** 지금 크기를 다시 알립니다. 소켓이 열린 뒤 한 번 부릅니다. */
  reportSize(): void;
  setScreenReaderMode(on: boolean): void;
  dispose(): void;
}

export interface TerminalOptions {
  onData(data: string): void;
  onResize(size: { cols: number; rows: number }): void;
  /** 키보드 탈출구. 터미널이 키를 전부 가져가므로 하나는 남겨야 합니다. */
  onEscapeHatch(): void;
}

/** 역할 토큰에서 색을 읽습니다. 값이 없으면 에뮬레이터 기본값을 씁니다. */
function themeFrom(el: HTMLElement) {
  const style = getComputedStyle(el);
  const token = (name: string) => style.getPropertyValue(name).trim() || undefined;
  return {
    background: token("--color-surface-2"),
    foreground: token("--color-text-primary"),
    cursor: token("--color-text-primary"),
    selectionBackground: token("--color-surface-hover"),
  };
}

export async function mountTerminal(host: HTMLElement, options: TerminalOptions): Promise<TerminalHandle> {
  const [{ Terminal }, { FitAddon }] = await Promise.all([
    import("@xterm/xterm"),
    import("@xterm/addon-fit"),
    import("@xterm/xterm/css/xterm.css"),
  ]);
  const style = getComputedStyle(host);
  const term = new Terminal({
    scrollback: SCROLLBACK,
    convertEol: false,
    cursorBlink: true,
    fontFamily: style.fontFamily,
    fontSize: Math.round(parseFloat(style.fontSize) || 13),
    theme: themeFrom(host),
    // 기본은 꺼짐입니다. 켜면 버퍼가 ARIA live 영역으로 노출됩니다(사용자 토글).
    screenReaderMode: false,
  });
  const fit = new FitAddon();
  term.loadAddon(fit);
  term.open(host);

  /* 셸이 쓰지 않는 조합 하나만 가로챕니다. `Esc`는 이제 vi를 쓰는 사람의 것이라
     패널을 닫는 데 쓸 수 없습니다. */
  term.attachCustomKeyEventHandler((e) => {
    if (e.type === "keydown" && e.ctrlKey && e.shiftKey && (e.key === "Q" || e.key === "q")) {
      e.preventDefault();
      options.onEscapeHatch();
      return false;
    }
    return true;
  });

  term.onData(options.onData);

  const report = () => {
    try {
      fit.fit();
    } catch {
      // 패널이 아직 0px이면 fit이 던집니다. 다음 관찰에서 다시 맞춥니다.
      return;
    }
    options.onResize({ cols: term.cols, rows: term.rows });
  };
  report();

  /* 창 크기가 아니라 **상자 크기**를 봅니다 — 패널이 열리고 닫힐 때도 맞아야 합니다. */
  const observer = new ResizeObserver(() => report());
  observer.observe(host);

  /* Light/Dark 전환은 토큰 값만 바꾸므로 에뮬레이터에 다시 읽어 넘깁니다. */
  const theme = window.matchMedia?.("(prefers-color-scheme: dark)");
  const onTheme = () => term.options.theme = themeFrom(host);
  theme?.addEventListener?.("change", onTheme);
  const attr = new MutationObserver(onTheme);
  attr.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

  return {
    write: (chunk) => term.write(chunk),
    focus: () => term.focus(),
    reset: () => term.reset(),
    reportSize: report,
    setScreenReaderMode: (on) => (term.options.screenReaderMode = on),
    dispose: () => {
      observer.disconnect();
      attr.disconnect();
      theme?.removeEventListener?.("change", onTheme);
      term.dispose();
    },
  };
}
